/*
 * product-pplr-price.js  (Bujoro, added 2026-09-14)
 *
 * Keeps every price on the product page in sync with the "Product Personalizer"
 * app (zeptoapps, window.pplr_*).
 *
 * Background: on most Bujoro products the Silver / Gold / Rose gold variants
 * all carry the SAME Shopify price. The gold up-charge (14K Gold, 18K Gold...)
 * is an app option with a "data-pplr_price" add-on. The app only writes its
 * total into the element matched by its own price selector (".price-item--sale"
 * inside the main price block). It never touches:
 *   - the regular / compare-at price and the "% OFF" badge in that block,
 *   - the sticky cart bar price (snippets/cart-bar.liquid, [data-product-price]),
 *   - the price inside the Add-to-cart button (snippets/product-buttons.liquid),
 * and when the theme swaps those blocks on a variant change the app total is
 * lost again. So the shopper sees the Silver price until the item is in the cart.
 *
 * How this works: the app exposes window.PPLR_CAlCULATE_PRICE (sic), which it
 * calls on every option change and which returns the add-on per unit in the
 * active currency. We wrap that function so we learn the add-on every time it
 * runs, then write "variant price + add-on" into all three places, and we ask
 * the app to recalculate after the theme's own variant / quantity updates.
 *
 * If the app is not installed on a product nothing here runs.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'theme:pplr:price';

  // Set to false to leave the compare-at ("was") price untouched when an add-on
  // is applied. With true the add-on is added to the compare-at as well, so the
  // struck-through price is never lower than the price being charged and the
  // "% OFF" badge is recalculated on the real numbers.
  var COMPARE_AT_INCLUDES_ADDON = true;

  var lastAddOn = 0; // per unit, decimal, active currency (what the app returns)

  /* ---------- 1. learn the add-on from the app ---------- */

  function wrapCalculator(fn) {
    if (typeof fn !== 'function' || fn.__themePplrWrapped) return fn;
    var wrapped = function () {
      var result = fn.apply(this, arguments);
      var addOn = parseFloat(result);
      lastAddOn = isNaN(addOn) ? 0 : addOn;
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { addOn: lastAddOn } }));
      return result;
    };
    wrapped.__themePplrWrapped = true;
    return wrapped;
  }

  var stored = wrapCalculator(window.PPLR_CAlCULATE_PRICE);
  try {
    // The app assigns window.PPLR_CAlCULATE_PRICE after its script loads; the
    // setter wraps it whenever that happens.
    Object.defineProperty(window, 'PPLR_CAlCULATE_PRICE', {
      configurable: true,
      enumerable: true,
      get: function () { return stored; },
      set: function (fn) { stored = wrapCalculator(fn); }
    });
  } catch (e) {
    /* defineProperty refused: recalc() below still reads the return value itself */
  }

  // Ask the app for a fresh total. Normally this goes through the wrapper,
  // which dispatches the sync event; if the function could not be wrapped we
  // dispatch from the return value here instead.
  function recalc() {
    var fn = window.PPLR_CAlCULATE_PRICE;
    if (typeof fn !== 'function') return;
    try {
      var result = fn();
      if (!fn.__themePplrWrapped) {
        var addOn = parseFloat(result);
        lastAddOn = isNaN(addOn) ? 0 : addOn;
        document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { addOn: lastAddOn } }));
      }
    } catch (e) { /* app error: leave theme prices alone */ }
  }

  /* ---------- 2. write the total into the theme's price elements ---------- */

  function money(cents) {
    if (cents === 0 && window.theme && window.theme.strings && window.theme.strings.free) {
      return window.theme.strings.free;
    }
    return window.theme.formatMoney(cents, window.theme.moneyFormat);
  }

  function currentVariant(info) {
    // Preferred: the JSON the theme renders in the variant picker (presentment currency).
    var json = info.querySelector('variant-selects [data-selected-variant]');
    if (json) {
      try { return JSON.parse(json.textContent); } catch (e) { /* fall through */ }
    }
    // Single-variant products have no picker: use the app's product JSON.
    var idInput = info.querySelector('[data-product-form] input[name="id"], form[action*="/cart/add"] input[name="id"]');
    var id = idInput && parseInt(idInput.value, 10);
    var product = window.pplr_product;
    if (!id || !product || !product.variants) return null;
    var rate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (v.id === id) {
        return {
          id: v.id,
          price: Math.round(v.price * rate),
          compare_at_price: v.compare_at_price ? Math.round(v.compare_at_price * rate) : null
        };
      }
    }
    return null;
  }

  function sync(addOn) {
    var info = document.querySelector('product-info');
    if (!info) return;
    var variant = currentVariant(info);
    if (!variant) return;

    var addOnCents = Math.round((parseFloat(addOn) || 0) * 100);
    var price = (variant.price || 0) + addOnCents;
    var compare = variant.compare_at_price || 0;
    if (compare && COMPARE_AT_INCLUDES_ADDON) compare += addOnCents;
    var onSale = compare > price;

    var qtyInput = info.querySelector('[data-quantity-input]');
    var qty = (qtyInput && parseInt(qtyInput.value, 10)) || 1;

    // (a) main price block (snippets/price.liquid)
    var block = info.querySelector('[id^="Price-"]');
    if (block && block.querySelector('.price-item')) {
      block.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach(function (el) {
        el.textContent = money(price);
      });
      block.querySelectorAll('.price__sale s.price-item--regular').forEach(function (el) {
        el.textContent = money(compare);
      });
      var badge = block.querySelector('[data-price-off-badge] > span');
      if (badge) {
        if (onSale) {
          badge.textContent = /%/.test(badge.textContent)
            ? Math.round((compare - price) * 100 / compare) + '%'
            : money(compare - price);
        }
        badge.closest('[data-price-off-badge]').style.display = onSale ? '' : 'none';
      }
      var sale = block.querySelector('.price__sale');
      if (sale) sale.style.display = onSale ? '' : 'none';
    }

    // (b) sticky cart bar (snippets/cart-bar.liquid)
    info.querySelectorAll('cart-bar [data-product-price]').forEach(function (el) {
      el.textContent = money(price);
      el.classList.toggle('product__price--sale', onSale);
    });
    info.querySelectorAll('cart-bar .product__price--strike').forEach(function (el) {
      el.textContent = onSale ? money(compare) : '';
    });

    // (c) price inside the Add-to-cart button (snippets/product-buttons.liquid).
    //     Mirrors product-info.js updateButtonPrice(): unit price x quantity.
    info.querySelectorAll('[data-add-to-cart] [data-product-price]').forEach(function (el) {
      var html = money(price * qty);
      if (onSale) html += ' <s>' + money(compare * qty) + '</s>';
      el.innerHTML = html;
    });
  }

  document.addEventListener(EVENT_NAME, function (e) {
    sync(e.detail && e.detail.addOn);
  });

  /* ---------- 3. re-apply after the theme rewrites its own blocks ---------- */

  function scheduleRecalc() {
    // Let the theme (product-info.js) finish its own DOM update first.
    window.requestAnimationFrame(function () { window.setTimeout(recalc, 0); });
  }

  function bind() {
    // Theme variant change: product-info.js has just swapped Price / CartBar
    // and reset the button price to the bare variant price.
    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, scheduleRecalc);
      window.subscribe(window.theme.PUB_SUB_EVENTS.quantityUpdate, scheduleRecalc);
    }
    // Quantity input: updateButtonPrice() runs on 'change' and drops the add-on.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-quantity-input]')) scheduleRecalc();
    });
    // The app builds its options after load; when it announces itself, sync once
    // so a pre-selected priced option is reflected everywhere.
    ['pplrAppBuild', 'pplrAppInitiated'].forEach(function (name) {
      window.addEventListener(name, scheduleRecalc);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
